import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { deleteJson, postJson } from "../../utils/api";
import { FeedbackGroup, FeedbackVariation } from "../../models";

const ADMIN = "/admin";
const SLUG_RE = /[^a-z0-9-]/g;
const ANIM_RE = /[^a-z0-9_]/g;

interface Props {
  curriculumId: string;
  feedbackGroups: FeedbackGroup[];
  onRefresh: () => Promise<void>;
}

export const FeedbackGroupsPanel: React.FC<Props> = ({
  curriculumId,
  feedbackGroups,
  onRefresh,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newId, setNewId] = useState("");
  const [newType, setNewType] = useState("CORRECT");
  const [newVariationText, setNewVariationText] = useState("");
  const [newVariationAnim, setNewVariationAnim] = useState("");
  const [newVariations, setNewVariations] = useState<FeedbackVariation[]>([]);

  const handleArchive = async (groupId: string) => {
    if (!window.confirm(`Archive feedback group "${groupId}"?`)) return;
    try {
      await deleteJson(
        `${ADMIN}/curricula/${curriculumId}/feedback-groups/${groupId}`
      );
      await onRefresh();
    } catch (e: any) {
      alert(e?.error?.detail ?? "Failed to archive feedback group");
    }
  };

  const handleAddVariation = () => {
    if (!newVariationText.trim()) return;
    const variation: FeedbackVariation = {
      id: `v-${Date.now()}`,
      text: newVariationText.trim(),
    };
    if (newVariationAnim.trim()) {
      variation.animation_id = newVariationAnim.trim();
    }
    setNewVariations((prev) => [...prev, variation]);
    setNewVariationText("");
    setNewVariationAnim("");
  };

  const handleCreate = async () => {
    if (!newId || newVariations.length === 0) return;
    try {
      await postJson(`${ADMIN}/curricula/${curriculumId}/feedback-groups`, {
        id: newId,
        type: newType,
        variations: newVariations,
      });
      setCreateOpen(false);
      setNewId("");
      setNewType("CORRECT");
      setNewVariations([]);
      await onRefresh();
    } catch (e: any) {
      alert(e?.error?.detail ?? "Failed to create feedback group");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6">
          Feedback Groups ({feedbackGroups.length})
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Add Group
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Feedback groups define the messages shown to users after answering a
        question. Each group has a type (Correct or Incorrect) and multiple
        variations — the app randomly picks one variation each time so feedback
        feels less repetitive. Each variation can include a Fez animation.
        Questions reference one correct and one incorrect group.
      </Typography>

      {feedbackGroups.map((fg) => (
        <Card key={fg.id} sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={() => setExpanded(expanded === fg.id ? null : fg.id)}
              >
                {expanded === fg.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Typography sx={{ ml: 1, flex: 1 }}>{fg.id}</Typography>
              <Chip
                label={fg.type}
                color={fg.type === "CORRECT" ? "success" : "warning"}
                size="small"
                sx={{ mr: 1 }}
              />
              <Chip
                label={`${fg.variations.length} variations`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <Chip
                label={`${fg.usage_count} refs`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <IconButton
                size="small"
                color="error"
                disabled={fg.usage_count > 0}
                onClick={() => handleArchive(fg.id)}
                title={
                  fg.usage_count > 0
                    ? "Cannot archive: referenced by nodes"
                    : "Archive"
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Collapse in={expanded === fg.id}>
              <List dense disablePadding sx={{ ml: 4 }}>
                {fg.variations.map((v, idx) => (
                  <ListItem key={v.id || idx}>
                    <ListItemText
                      primary={v.text}
                      secondary={
                        v.animation_id ? `${v.id} · ${v.animation_id}` : v.id
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </CardContent>
        </Card>
      ))}

      {feedbackGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No feedback groups yet.
        </Typography>
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Feedback Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ID (slug)"
            fullWidth
            value={newId}
            onChange={(e) =>
              setNewId(e.target.value.toLowerCase().replace(SLUG_RE, ""))
            }
          />
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            select
            SelectProps={{ native: true }}
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
          >
            <option value="CORRECT">Correct</option>
            <option value="INCORRECT">Incorrect</option>
          </TextField>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Variations
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              size="small"
              sx={{ flex: 2 }}
              placeholder="Variation text..."
              value={newVariationText}
              onChange={(e) => setNewVariationText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddVariation()}
            />
            <TextField
              size="small"
              sx={{ flex: 1 }}
              placeholder="Animation ID (optional)"
              value={newVariationAnim}
              onChange={(e) =>
                setNewVariationAnim(
                  e.target.value.toLowerCase().replace(ANIM_RE, "")
                )
              }
              onKeyDown={(e) => e.key === "Enter" && handleAddVariation()}
            />
            <Button size="small" onClick={handleAddVariation}>
              Add
            </Button>
          </Box>
          {newVariations.map((v, idx) => (
            <Chip
              key={idx}
              label={v.animation_id ? `${v.text} (${v.animation_id})` : v.text}
              onDelete={() =>
                setNewVariations((prev) => prev.filter((_, i) => i !== idx))
              }
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newId || newVariations.length === 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
